import { Title, LoadingOverlay, Container, Alert } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import Breadcrumbs from 'components/BreadcrumbsComponent';
import { BreadcrumbItem } from 'components/BreadcrumbsComponent';
import CoursePlanForm from 'components/forms/lcms/CoursePlanForm';
import React, { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { pathnames } from 'routes/lcms';
import { coursePlanService } from 'services/lcms/coursePlanService';
import { useCoursePlanStore } from 'store/createCoursePlan';
import type {
  CoursePlanCourseForm,
  CoursePlanTargetPositionForm,
  CoursePlanBusinessUnitForm,
  CoursePlanJobFamilyGroupForm,
  CoursePlanJobFamilyForm
} from 'types/lcms/coursePlan';

const EditCoursePlanPage = () => {
  const { id } = useParams<{ id: string }>();
  const { setFormData, setFormMode } = useCoursePlanStore();
  const dataSetRef = useRef(false);

  const {
    data: coursePlanDetail,
    isLoading,
    error
  } = useQuery({
    queryKey: ['coursePlanDetail', id],
    queryFn: () => coursePlanService.getCoursePlanDetail(id!),
    enabled: !!id,
    refetchOnWindowFocus: false
  });

  useEffect(() => {
    if (coursePlanDetail?.data && !dataSetRef.current) {
      const detail = coursePlanDetail.data;

      const formatDateForInput = (dateString: string) => {
        if (!dateString) return '';
        return dateString.split('T')[0];
      };

      // Map courses from API response to form structure
      const mappedCourses: CoursePlanCourseForm[] = Object.values(
        (detail.courses || []).reduce<Record<string, CoursePlanCourseForm>>((acc, course) => {
          const courseId = course.msLCMSCourseID;
          if (!courseId || acc[courseId]) return acc;

          acc[courseId] = {
            courseId,
            courseName: course.courseName,
            courseCode: course.courseCode || '',
            deliveryType: course.deliveryType,
            learningHours: course.learningHours,
            modeOfDelivery: course.modeOfDelivery || 'N/A',
            trLCMSPlanningCoursesID: course.trLCMSPlanningCoursesID
          };

          return acc;
        }, {})
      );

      // Map target positions from API response to form structure
      const mappedTargetPositions: CoursePlanTargetPositionForm[] =
        detail.targetPositions?.map((position) => ({
          positionId: position.positionID,
          positionName: position.positionName,
          trLCMSPlanningTargetPositionsID: position.trLCMSPlanningTargetPositionsID
        })) || [];

      // Update business units mapping to use the new API structure
      const mappedBusinessUnits: CoursePlanBusinessUnitForm[] =
        detail.businessUnits?.map((bu) => ({
          businessUnitId: bu.businessUnitID, // Use businessUnitID from API
          businessUnitName: bu.businessUnitName, // Use businessUnitName from API
          trLCMSPlanningBusinessUnitsID: bu.trLCMSPlanningBusinessUnitsID
        })) || [];

      // Update job family groups mapping to use the new API structure
      const mappedJobFamilyGroups: CoursePlanJobFamilyGroupForm[] =
        detail.jobFamilyGroups?.map((jfg) => ({
          jobFamilyGroupId: jfg.jobFamilyGroupID, // Use direct field from API
          jobFamilyGroupName: jfg.jobFamilyGroupName, // Use direct field from API
          trLCMSPlanningJobFamilyGroupsID: jfg.trLCMSPlanningJobFamilyGroupsID
        })) || [];

      // Update job families mapping to include group relationship
      const mappedJobFamilies: CoursePlanJobFamilyForm[] =
        detail.jobFamilies?.map((jf) => ({
          jobFamilyId: jf.jobFamilyID, // Use direct field from API
          jobFamilyName: jf.jobFamilyName, // Use direct field from API
          jobFamilyGroupId: jf.jobFamilyGroupID, // Use direct field from API
          jobFamilyGroupName: jf.jobFamilyGroupName, // Use direct field from API
          trLCMSPlanningJobFamiliesID: jf.trLCMSPlanningJobFamiliesID
        })) || [];

      const formDataToSet = {
        courseId: detail.msLCMSPlanningID,
        planName: detail.planningName,
        programType: detail.programType.toLowerCase(),
        description: detail.planningDescription || '',
        startTime: formatDateForInput(detail.planningStart),
        endTime: formatDateForInput(detail.planningEnd),
        prerequisites: detail.prerequisites || '',
        learningMaterials: detail.learningMaterials || '',
        facilities: detail.facilitiesRequired || '',
        notes: detail.notes || '',
        courses: mappedCourses,
        targetPositions: mappedTargetPositions,
        businessUnits: mappedBusinessUnits,
        jobFamilyGroups: mappedJobFamilyGroups,
        jobFamilies: mappedJobFamilies,
        targetPosition: detail.targetPositions?.map((pos) => pos.positionName).join(', ') || ''
      };

      setFormMode('edit');
      setFormData(formDataToSet);
      dataSetRef.current = true;
    }
  }, [coursePlanDetail, setFormData, setFormMode]);

  useEffect(() => {
    dataSetRef.current = false;
  }, [id]);

  const breadcrumbs: BreadcrumbItem[] = [
    { title: 'Course Planning', href: pathnames.coursePlan },
    { title: 'Edit Course Plan', href: '' }
  ];

  if (error) {
    return (
      <Container fluid>
        <Breadcrumbs items={breadcrumbs} mb={32} />
        <Alert icon={<IconAlertCircle size={16} />} title='Error' color='red'>
          Failed to load course plan details. Please try again later.
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid pos='relative'>
      <LoadingOverlay visible={isLoading} />

      <Breadcrumbs items={breadcrumbs} mb={32} />

      <Title order={2} mb={30}>
        Edit Course Plan
      </Title>

      {coursePlanDetail?.data && <CoursePlanForm />}
    </Container>
  );
};

export default EditCoursePlanPage;
